import type { RefObject } from 'react';
import type { CtaRefType } from 'util/cta/cta.data';
import { findTransitionController } from '../../../util/core-transition-component';
import { fadeFromTo, fadeTo } from '../../../util/animation/fade/fade';

interface PrimaryButtonRefs {
  elementRef?: RefObject<CtaRefType | null>;
  labelRef?: RefObject<HTMLSpanElement | null>;
  contentRef?: RefObject<HTMLDivElement | null>;
  hoverLabelRef?: RefObject<HTMLSpanElement | null>;
  backgroundHoverRef?: RefObject<HTMLDivElement | null>;
  noTransition?: boolean;
}

export function setupTransitionInTimeline(
  timeline: gsap.core.Timeline,
  { elementRef, labelRef, noTransition }: PrimaryButtonRefs,
): void {
  if (!noTransition) {
    const labelController = findTransitionController(labelRef);

    if (elementRef?.current) {
      timeline.fromTo(
        elementRef.current,
        { scaleX: 0.4 },
        { duration: 0.5, scaleX: 1, ease: 'Expo.easeOut', clearProps: 'scale' },
        0,
      );
      timeline.add(fadeFromTo(elementRef.current as HTMLElement), 0);
    }

    if (labelController) {
      timeline.add(labelController.getTimeline('in') as gsap.core.Timeline, 0);
    }
  }
}

export function setupTransitionOutTimeline(
  timeline: gsap.core.Timeline,
  { elementRef }: PrimaryButtonRefs,
): void {
  if (elementRef?.current) {
    timeline.add(
      fadeTo(elementRef.current as HTMLElement, {
        duration: 0.4,
        ease: 'Expo.easeOut',
      }),
    );
    timeline.to(elementRef.current, { duration: 0.4, scaleX: 0.4, ease: 'Expo.easeOut' }, 0);
  }
}

export function setupHoverTransitionTimeline(
  timeline: gsap.core.Timeline,
  { hoverLabelRef, contentRef, backgroundHoverRef }: PrimaryButtonRefs,
): void {
  if (hoverLabelRef?.current) {
    timeline.fromTo(
      hoverLabelRef.current,
      {
        yPercent: 400,
        rotate: 30,
        z: 1,
      },
      {
        yPercent: 0,
        rotate: 0,
        z: 1,
        duration: 0.45,
        ease: 'Power1.easeOut',
      },
      0,
    );
  }

  if (contentRef?.current) {
    timeline.fromTo(
      contentRef.current,
      {
        yPercent: 0,
        rotate: 0,
        z: 1,
      },
      {
        yPercent: -400,
        rotate: 20,
        z: 1,
        duration: 0.45,
        ease: 'Power1.easeIn',
      },
      0,
    );
  }

  if (backgroundHoverRef?.current) {
    timeline.fromTo(
      backgroundHoverRef.current,
      {
        yPercent: 75,
      },
      {
        yPercent: 0,
        duration: 0.6,
        ease: 'Power3.easeInOut',
      },
      0,
    );
  }
}
